import React, { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogType?: string;
  ogImage?: string;
  schemaJson?: object | object[];
  schema?: object | object[];
}

export const SEO: React.FC<SEOProps> = ({
  title,
  description,
  keywords = 'Shakti Group, Industrial Gases, Cryogenic Gases, Liquid Oxygen, Liquid Nitrogen, Argon, CO2, PESO Certified, Gujarat India',
  canonicalUrl,
  ogType = 'website',
  ogImage = 'https://qe2eq2zzuxmkvacf.public.blob.vercel-storage.com/product_hero.png',
  schemaJson,
  schema
}) => {
  const currentUrl = canonicalUrl || (typeof window !== 'undefined' ? `${window.location.origin}${window.location.pathname}` : 'https://www.shaktigases.com');
  const activeSchema = schemaJson || schema;

  useEffect(() => {
    // 1. Update Document Title
    document.title = title;

    // Helper to set or update meta tag
    const setMetaTag = (selector: string, attributeName: string, attributeValue: string, content: string) => {
      let element = document.querySelector(selector);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attributeName, attributeValue);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    // Helper to set or update link tag
    const setLinkTag = (rel: string, href: string) => {
      let element = document.querySelector(`link[rel="${rel}"]`);
      if (!element) {
        element = document.createElement('link');
        element.setAttribute('rel', rel);
        document.head.appendChild(element);
      }
      element.setAttribute('href', href);
    };

    // Standard Meta Tags
    setMetaTag('meta[name="description"]', 'name', 'description', description);
    setMetaTag('meta[name="keywords"]', 'name', 'keywords', keywords);

    // Canonical Tag
    setLinkTag('canonical', currentUrl);

    // OpenGraph Meta Tags
    setMetaTag('meta[property="og:title"]', 'property', 'og:title', title);
    setMetaTag('meta[property="og:description"]', 'property', 'og:description', description);
    setMetaTag('meta[property="og:url"]', 'property', 'og:url', currentUrl);
    setMetaTag('meta[property="og:type"]', 'property', 'og:type', ogType);
    setMetaTag('meta[property="og:image"]', 'property', 'og:image', ogImage);
    setMetaTag('meta[property="og:site_name"]', 'property', 'og:site_name', 'Shakti Group');

    // Twitter Card Meta Tags
    setMetaTag('meta[name="twitter:card"]', 'name', 'twitter:card', 'summary_large_image');
    setMetaTag('meta[name="twitter:title"]', 'name', 'twitter:title', title);
    setMetaTag('meta[name="twitter:description"]', 'name', 'twitter:description', description);
    setMetaTag('meta[name="twitter:image"]', 'name', 'twitter:image', ogImage);

    // Schema.org JSON-LD Injection
    let scriptTag = document.querySelector('script[id="json-ld-schema"]') as HTMLScriptElement | null;
    if (activeSchema) {
      if (!scriptTag) {
        scriptTag = document.createElement('script');
        scriptTag.id = 'json-ld-schema';
        scriptTag.type = 'application/ld+json';
        document.head.appendChild(scriptTag);
      }
      scriptTag.textContent = JSON.stringify(activeSchema);
    } else if (scriptTag) {
      scriptTag.remove();
    }
  }, [title, description, keywords, currentUrl, ogType, ogImage, activeSchema]);

  return (
    <>
      {/* Fallback React 19 title/meta rendering */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
    </>
  );
};

export default SEO;
